import { formatInAppTz } from '@/lib/date';

import type { OrderReplacementDetail } from './types';

export type OrderReplacementTimelineEvent = { label: string; at: string };

/** Timeline stamp per AC 112368 + all three Figma frames: `20 Jan 2025, 07:30 PM`. The shipped
 *  `formatReplacementDateTime` uses a ` | ` separator, which is right for the queue drawer's
 *  "Order Date & Time" field but contradicts both sources here. Day is unpadded per app-wide
 *  convention — the AC's "DD" is loose notation and Figma's 2-digit sample cannot disambiguate. */
export const formatTimelineStamp = (iso: string): string =>
  formatInAppTz(iso, 'D MMM YYYY, hh:mm A');

/**
 * The Activity Timeline, derived client-side (ADR-0022, grilling decision 13): the detail payload
 * carries no events array. `Created` and `Pending` share `requestedAt` — Figma stamps them
 * identically, which is one event rendered twice, exactly as the AC's "immediately transitions to
 * Pending" describes. The terminal entry lands at `resolvedAt`.
 *
 * BE confirmed `resolvedAt` is always set once a request is resolved (ADO 112368, 2026-07-29), but
 * the guard stays: a null must drop the entry, never render `Invalid Date`. If BE later ships a real
 * `timeline[]`, replace this call with the served array — `<Timeline>` renders both identically.
 */
export function deriveReplacementTimeline(
  detail: Pick<OrderReplacementDetail, 'status' | 'requestedAt' | 'resolvedAt'>,
): OrderReplacementTimelineEvent[] {
  const events: OrderReplacementTimelineEvent[] = [
    { label: 'Created', at: detail.requestedAt },
    { label: 'Pending', at: detail.requestedAt },
  ];
  if (detail.status !== 'pending' && detail.resolvedAt) {
    events.push({
      label: detail.status === 'approved' ? 'Approved' : 'Rejected',
      at: detail.resolvedAt,
    });
  }
  return events;
}
